import type { ReactNode } from 'react';
import styles from './Table.module.css';

interface TableColumn<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  width?: string;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  className?: string;
}

/**
 * Accessible table component for displaying structured data.
 *
 * - Uses semantic <table> element with <thead> and <tbody>.
 * - Supports row click handlers for navigation.
 * - Responsive: horizontally scrollable on small screens.
 * - Keyboard accessible: rows with onClick are focusable and activated with Enter.
 */
export function Table<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyMessage = 'Nenhum item encontrado.',
  className,
}: TableProps<T>) {
  const wrapperClassNames = [styles.wrapper, className ?? ''].filter(Boolean).join(' ');

  return (
    <div className={wrapperClassNames}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={styles.th}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {data.length === 0 ? (
            <tr>
              <td className={styles.emptyCell} colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item) => {
              const key = keyExtractor(item);
              const isClickable = Boolean(onRowClick);

              return (
                <tr
                  key={key}
                  className={[styles.tr, isClickable ? styles.clickable : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={onRowClick ? () => onRowClick(item) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === 'Enter') {
                            onRowClick(item);
                          }
                        }
                      : undefined
                  }
                  tabIndex={isClickable ? 0 : undefined}
                  role={isClickable ? 'button' : undefined}
                >
                  {columns.map((column) => (
                    <td key={column.key} className={styles.td}>
                      {column.render(item)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
